import CancelListing from '@/components/buttons/CancelListing';
import EditListing from '@/components/buttons/EditListing';
import { useMarketplaceChain } from '@/hooks/use-marketplace-chain';
import { useUserListings } from '@/hooks/use-user-listings';
import { useAuthentication } from '@/hooks/useAuthentication';
import { TokenSymbol } from '@/types/tokens';
import { routes } from '@/utils/routes';
import { Button, Flex, Td, Text } from '@chakra-ui/react';
import { compareAddresses, formatAddress } from '@onbeam/utils';
import { ListedIcon } from '@sphere/icons';
import useTranslation from 'next-translate/useTranslation';
import { rem } from 'polished';
import { AccountTableItemDesktop } from '../AccountTableItemDesktop';

export const AccountListingItemDesktop = ({
  listing,
  refetch,
}: {
  listing: ReturnType<typeof useUserListings>['data'][number];
  refetch: () => void;
}) => {
  const marketplaceChain = useMarketplaceChain();
  const { t } = useTranslation('account');
  const { userAddress } = useAuthentication();

  const isOwner = compareAddresses(userAddress, listing.maker);
  const collectionId = listing.criteria?.data?.collection?.id || listing.contract || '';

  return (
    <AccountTableItemDesktop
      mediaItemProps={{
        href: routes.nft.detail(
          collectionId,
          listing.criteria?.data?.token?.tokenId ?? '',
          marketplaceChain.routePrefix,
        ),
        title: listing.criteria?.data?.token?.name || `#${listing.criteria?.data?.token?.tokenId}`,
        imageSrc: listing.criteria?.data?.token?.image || listing.criteria?.data?.collection?.image,
        subtitle: (
          <Text fontSize="sm" color="gray.5" noOfLines={1} wordBreak="break-all">
            {formatAddress(listing.criteria?.data?.collection?.name)}
          </Text>
        ),
        quantity: listing.quantityRemaining,
        ...(!listing.criteria?.data?.token?.image &&
          listing.criteria?.data?.collection?.image && { rounded: 'full', coverImage: true }),
      }}
      icon={{
        Component: ListedIcon,
        label: t('listings.table.type.fixed-price'),
      }}
      currency={listing.price?.currency?.symbol as TokenSymbol}
      tokenAmount={listing.price?.amount?.decimal}
      usdAmount={listing.price?.amount?.usd}
      startTime={listing.createdAt}
      endTime={listing.validUntil ? new Date(listing.validUntil * 1000).toISOString() : undefined}
    >
      {isOwner && (
        <Td textAlign="right">
          <Flex gap="space.4" justify="end" wrap={['wrap', null, null, 'unset']}>
            {/* Only Oracle listings can be edited */}
            {listing.isNativeOffChainCancellable && (
              <EditListing
                listingId={listing.id}
                tokenId={listing.criteria?.data?.token?.tokenId}
                collectionId={collectionId}
                mutate={refetch}
                trigger={
                  <Button
                    variant="secondary"
                    py="space.4"
                    px="space.24"
                    fontSize="sm"
                    lineHeight={rem(28)}
                    w={rem(100)}
                  >
                    {t('listings.table.item.edit')}
                  </Button>
                }
              />
            )}
            <CancelListing
              listingId={listing.id}
              mutate={refetch}
              trigger={
                <Button
                  variant="secondary"
                  py="space.4"
                  px="space.24"
                  fontSize="sm"
                  lineHeight={rem(28)}
                  w={rem(100)}
                >
                  {t('listings.table.item.cancel')}
                </Button>
              }
            />
          </Flex>
        </Td>
      )}
    </AccountTableItemDesktop>
  );
};
